import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import './PrivateJetsPage.css';

const PrivateJetsPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    from: '',
    to: '',
    departureDate: '',
    returnDate: '',
    passengers: 1,
    aircraftType: '',
    amenities: [],
    catering: 'Standard',
    specialRequests: ''
  });

  const nigerianAirports = [
    { code: 'LOS', name: 'Lagos (LOS)', city: 'Lagos' },
    { code: 'ABV', name: 'Abuja (ABV)', city: 'Abuja' },
    { code: 'PHC', name: 'Port Harcourt (PHC)', city: 'Port Harcourt' },
    { code: 'KAN', name: 'Kano (KAN)', city: 'Kano' },
    { code: 'ENU', name: 'Enugu (ENU)', city: 'Enugu' },
    { code: 'BNI', name: 'Benin (BNI)', city: 'Benin' }
  ];

  const aircraftTypes = [
    { id: 'light', name: 'Light Jet', capacity: '4-6 passengers', price: 'From ₦2,500,000', icon: '✈️' },
    { id: 'midsize', name: 'Midsize Jet', capacity: '6-8 passengers', price: 'From ₦4,500,000', icon: '🛩️' },
    { id: 'super-midsize', name: 'Super Midsize Jet', capacity: '8-10 passengers', price: 'From ₦7,500,000', icon: '✈️' },
    { id: 'large', name: 'Large Jet', capacity: '10-14 passengers', price: 'From ₦12,000,000', icon: '🛫' },
    { id: 'ultra-long', name: 'Ultra Long Range', capacity: '12-16 passengers', price: 'From ₦18,000,000', icon: '🛬' }
  ];

  const amenitiesOptions = [
    'Wi-Fi',
    'Entertainment System',
    'Conference Table',
    'Private Bedroom',
    'Shower',
    'Gourmet Kitchen',
    'Bar',
    'Medical Equipment'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAmenityToggle = (amenity) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Navigate to booking confirmation or payment
    navigate('/payment', { state: { bookingType: 'private-jet', formData } });
  };

  return (
    <div className="private-jets-page">
      <Header />
      
      {/* Hero Section */}
      <section className="private-jets-hero-section">
        <div className="page-container">
          <div className="private-jets-hero-content">
            <h1 className="private-jets-hero-title">Private Jet Charter</h1>
            <p className="private-jets-hero-subtitle">
              Experience the ultimate in luxury air travel. Book your private jet charter with premium service and unmatched comfort.
            </p>
          </div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section className="private-jets-form-section">
        <div className="page-container">
          <div className="private-jets-form-card">
            <div className="form-header">
              <div className="form-header-icon">🛩️</div>
              <h2 className="form-header-title">Request Your Private Jet</h2>
              <p className="form-header-subtitle">Fill in the details below to get a personalized quote</p>
            </div>

            <form onSubmit={handleSubmit} className="private-jets-form">
              {/* Route Information */}
              <div className="form-section">
                <h3 className="form-section-title">Route Information</h3>
                <div className="form-fields-grid">
                  <div className="form-field-card">
                    <label className="field-label">
                      <span className="field-icon">📍</span>
                      Departure Airport
                    </label>
                    <select
                      name="from"
                      value={formData.from}
                      onChange={handleInputChange}
                      className="form-select"
                      required
                    >
                      <option value="">Select departure airport</option>
                      <optgroup label="Nigeria">
                        {nigerianAirports.map(airport => (
                          <option key={airport.code} value={airport.code}>
                            {airport.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="International">
                        <option value="LHR">London Heathrow (LHR)</option>
                        <option value="DXB">Dubai (DXB)</option>
                        <option value="JFK">New York JFK (JFK)</option>
                        <option value="CDG">Paris Charles de Gaulle (CDG)</option>
                        <option value="JNB">Johannesburg (JNB)</option>
                      </optgroup>
                    </select>
                  </div>

                  <div className="form-field-card">
                    <label className="field-label">
                      <span className="field-icon">✈️</span>
                      Destination Airport
                    </label>
                    <select
                      name="to"
                      value={formData.to}
                      onChange={handleInputChange}
                      className="form-select"
                      required
                    >
                      <option value="">Select destination airport</option>
                      <optgroup label="Nigeria">
                        {nigerianAirports.map(airport => (
                          <option key={airport.code} value={airport.code}>
                            {airport.name}
                          </option>
                        ))}
                      </optgroup>
                      <optgroup label="International">
                        <option value="LHR">London Heathrow (LHR)</option>
                        <option value="DXB">Dubai (DXB)</option>
                        <option value="JFK">New York JFK (JFK)</option>
                        <option value="CDG">Paris Charles de Gaulle (CDG)</option>
                        <option value="JNB">Johannesburg (JNB)</option>
                      </optgroup>
                    </select>
                  </div>

                  <div className="form-field-card">
                    <label className="field-label">
                      <span className="field-icon">📅</span>
                      Departure Date
                    </label>
                    <input
                      type="date"
                      name="departureDate"
                      value={formData.departureDate}
                      onChange={handleInputChange}
                      className="form-input"
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>

                  <div className="form-field-card">
                    <label className="field-label">
                      <span className="field-icon">📅</span>
                      Return Date (Optional)
                    </label>
                    <input
                      type="date"
                      name="returnDate"
                      value={formData.returnDate}
                      onChange={handleInputChange}
                      className="form-input"
                      min={formData.departureDate || new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>
              </div>

              {/* Aircraft Selection */}
              <div className="form-section">
                <h3 className="form-section-title">Select Aircraft Type</h3>
                <div className="aircraft-grid">
                  {aircraftTypes.map((aircraft) => (
                    <div
                      key={aircraft.id}
                      className={`aircraft-card ${formData.aircraftType === aircraft.id ? 'selected' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, aircraftType: aircraft.id }))}
                    >
                      <div className="aircraft-icon">{aircraft.icon}</div>
                      <h4 className="aircraft-name">{aircraft.name}</h4>
                      <p className="aircraft-capacity">{aircraft.capacity}</p>
                      <p className="aircraft-price">{aircraft.price}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Passengers */}
              <div className="form-section">
                <h3 className="form-section-title">Passenger Details</h3>
                <div className="form-fields-grid">
                  <div className="form-field-card">
                    <label className="field-label">
                      <span className="field-icon">👥</span>
                      Number of Passengers
                    </label>
                    <input
                      type="number"
                      name="passengers"
                      value={formData.passengers}
                      onChange={handleInputChange}
                      className="form-input"
                      min="1"
                      max="20"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Amenities */}
              <div className="form-section">
                <h3 className="form-section-title">Preferred Amenities</h3>
                <div className="amenities-grid">
                  {amenitiesOptions.map((amenity) => (
                    <label key={amenity} className="amenity-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.amenities.includes(amenity)}
                        onChange={() => handleAmenityToggle(amenity)}
                      />
                      <span className="amenity-label">{amenity}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Catering */}
              <div className="form-section">
                <h3 className="form-section-title">Catering Preference</h3>
                <div className="form-fields-grid">
                  <div className="form-field-card">
                    <label className="field-label">
                      <span className="field-icon">🍽️</span>
                      Catering Level
                    </label>
                    <select
                      name="catering"
                      value={formData.catering}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="Standard">Standard</option>
                      <option value="Premium">Premium</option>
                      <option value="Gourmet">Gourmet</option>
                      <option value="Custom">Custom Menu</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Special Requests */}
              <div className="form-section">
                <h3 className="form-section-title">Special Requests</h3>
                <div className="form-fields-grid">
                  <div className="form-field-card full-width">
                    <label className="field-label">
                      <span className="field-icon">📝</span>
                      Additional Requirements
                    </label>
                    <textarea
                      name="specialRequests"
                      value={formData.specialRequests}
                      onChange={handleInputChange}
                      className="form-textarea"
                      rows="4"
                      placeholder="Any special requests, dietary requirements, or additional information..."
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button type="submit" className="submit-jet-button">
                Request Quote
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="private-jets-benefits-section">
        <div className="page-container">
          <div className="benefits-header">
            <h2 className="benefits-title">Why Choose Private Jet Charter?</h2>
            <p className="benefits-subtitle">Experience the ultimate in luxury and convenience</p>
          </div>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">⏰</div>
              <h3 className="benefit-title">Flexible Scheduling</h3>
              <p className="benefit-text">Fly on your schedule, not airline schedules. Depart when you're ready.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">🔒</div>
              <h3 className="benefit-title">Privacy & Security</h3>
              <p className="benefit-text">Complete privacy for business meetings or personal travel.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">⭐</div>
              <h3 className="benefit-title">Premium Service</h3>
              <p className="benefit-text">Dedicated crew, gourmet catering, and personalized attention.</p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">🚀</div>
              <h3 className="benefit-title">Time Efficiency</h3>
              <p className="benefit-text">Skip long security lines and terminal waits. Arrive minutes before departure.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivateJetsPage;

